import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { Toast, ToastContainer, Modal } from "react-bootstrap";
import DataLoader from "../Spinner/DataLoader";
import { BASE_URL } from "../../constants";
import { apiFetch } from "../../utils/apiFetch";
import { useApiError } from "../../utils/useApiError";
import ValidationErrorModal from "../ValidationErrorModal/ValidationErrorModal";
import MarksPanel from "../MarksModal/MarksModal";
import ChangePassword from "../ChangePassword/ChangePassword";
import {
  formatDateTimeLocalValue,
  toIsoStringKeepingLocalTime,
} from "../../utils/datetime";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const filterOptions = ["Hamısı", "Aktiv", "Fasilə", "Passiv", "Məzun"];

const initialStudentColumns = [
  { id: "name", label: "Ad" },
  { id: "surname", label: "Soyad" },
  { id: "phone", label: "Telefon" },
  { id: "email", label: "Email" },
  { id: "username", label: "İstifadəçi adı" },
  { id: "studentId", label: "Kod" },
];

// ✅ DƏYİŞİKLİK 1: onCellClick prop əlavə edildi
function StudentSortableCol({
  col,
  students,
  selectedStudent,
  onSelect,
  onCellClick,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: col.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    display: "flex",
    flexDirection: "column",
    minWidth: 120,
    flex: 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div
        {...attributes}
        {...listeners}
        style={{
          padding: "10px 12px",
          height: 40,
          boxSizing: "border-box",
          background: "var(--bg-elevated)",
          color: "var(--text-muted)",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.6px",
          textTransform: "uppercase",
          borderBottom: "1px solid var(--border)",
          cursor: "grab",
          userSelect: "none",
          whiteSpace: "nowrap",
          position: "sticky",
          top: 0,
          zIndex: 2,
          display: "flex",
          alignItems: "center",
        }}
      >
        {col.label}
      </div>
      {students.map((st) => (
        <div
          key={st.id}
          // ✅ DƏYİŞİKLİK 2: onClick-ə onCellClick əlavə edildi
          onClick={(e) => {
            onSelect(st, e);
            onCellClick && onCellClick(st[col.id] || "");
          }}
          style={{
            padding: "0 12px",
            height: 40,
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            fontSize: 13,
            color: "var(--text-primary)",
            borderBottom: "1px solid var(--bg-elevated)",
            backgroundColor:
              selectedStudent?.id === st.id
                ? "rgba(37, 99, 235, 0.12)"
                : "transparent",
            cursor: "pointer",
            transition: "background-color 0.2s",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {st[col.id] || "—"}
        </div>
      ))}
    </div>
  );
}

// ✅ DƏYİŞİKLİK 3: onCellClick prop əlavə edildi
export default function StudentTab({
  groupId,
  styles: s,
  lessonLoading,
  onStartLesson,
  onCellClick,
  initialStudent,
  onBack,
}) {
  const tableScrollRef = useRef(null);
  const customScrollRef = useRef(null);
  const [tableScrollWidth, setTableScrollWidth] = useState(0);
  const syncingRef = useRef(false);
  const marksSaveRef = useRef(null);
  const marksRefreshRef = useRef(null);
  const [marksSaveStatus, setMarksSaveStatus] = useState(false);
  const { errorModal, clearError, handleApiError } = useApiError();

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    const update = () => setTableScrollWidth(el.scrollWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    const onScroll = () => {
      if (syncingRef.current) return;
      if (customScrollRef.current) {
        syncingRef.current = true;
        customScrollRef.current.scrollLeft = el.scrollLeft;
        syncingRef.current = false;
      }
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const handleCustomScrollX = () => {
    if (syncingRef.current) return;
    const el = tableScrollRef.current;
    if (el && customScrollRef.current) {
      syncingRef.current = true;
      el.scrollLeft = customScrollRef.current.scrollLeft;
      syncingRef.current = false;
    }
  };

  const [activeFilter, setActiveFilter] = useState("Aktiv");
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [studentColumns, setStudentColumns] = useState(initialStudentColumns);
  const [activeCol, setActiveCol] = useState(null);
  const [saveStatus, setSaveStatus] = useState(false);

  const [pendingStudent, setPendingStudent] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [marks, setMarks] = useState([]);
  const [marksLoading, setMarksLoading] = useState(false);
  const [marksVersion, setMarksVersion] = useState("v2");

  const [showPassModal, setShowPassModal] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [exitReason, setExitReason] = useState("");
  const [exitLoading, setExitLoading] = useState(false);

  const [helpSelectedIds, setHelpSelectedIds] = useState(new Set());
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpNotes, setHelpNotes] = useState({});
  const [helpLoading, setHelpLoading] = useState(false);
  const [helpBeginDate, setHelpBeginDate] = useState("");
  const [helpEndDate, setHelpEndDate] = useState("");
  const [helpLessonName, setHelpLessonName] = useState("");
  const [helpYoutubeLink, setHelpYoutubeLink] = useState("");
  const [helpLessonType, setHelpLessonType] = useState("Online");
  const [helpErrorModal, setHelpErrorModal] = useState(null);

  const toggleHelpSelect = (studentId) =>
    setHelpSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(studentId) ? next.delete(studentId) : next.add(studentId);
      return next;
    });

  const helpSelectedStudents = students.filter((st) =>
    helpSelectedIds.has(st.id),
  );

  const now = () => formatDateTimeLocalValue();

  const openHelpModal = () => {
    const n = now();
    setHelpBeginDate(n);
    setHelpEndDate(n);
    setHelpNotes({});
    setHelpLessonName("");
    setHelpYoutubeLink("");
    setHelpLessonType("Online");
    setShowHelpModal(true);
  };
  const closeHelpModal = () => {
    setShowHelpModal(false);
    setHelpNotes({});
  };

  const handleHelpSubmit = async () => {
    setHelpLoading(true);
    try {
      const res = await apiFetch(`${BASE_URL}/teacher-help-lessons`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          groupId: Number(groupId),
          beginDate: toIsoStringKeepingLocalTime(helpBeginDate),
          endDate: toIsoStringKeepingLocalTime(helpEndDate),
          lessonName: helpLessonName.trim(),
          youtubeLink: helpYoutubeLink.trim(),
          lessonType: helpLessonType,
          students: helpSelectedStudents.map((st) => ({
            contractId: st.contractId,
            teacherNotes: (helpNotes[st.contractId] || "").trim(),
          })),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setHelpErrorModal({
          message: data?.message ?? "Xəta baş verdi",
          validations: data?.validations ?? data?.errors ?? null,
        });
        return;
      }
      setHelpSelectedIds(new Set());
      closeHelpModal();
    } catch {
      setHelpErrorModal({
        message: "Şəbəkə xətası baş verdi",
        validations: null,
      });
    } finally {
      setHelpLoading(false);
    }
  };

  const handleSubmitExitReason = async () => {
    if (!exitReason.trim() || !pendingStudent?.contractId) return;
    setExitLoading(true);
    try {
      const res = await apiFetch(`${BASE_URL}/contracts/add-exit-reason`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contractId: pendingStudent.contractId,
          reason: exitReason.trim(),
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        handleApiError({
          message: d?.message ?? "Səbəb göndərilərkən xəta baş verdi.",
          validations: d?.validations,
        });
        return;
      }
      setShowExitModal(false);
      setExitReason("");
    } catch {
      handleApiError({ message: "Səbəb göndərilərkən xəta baş verdi." });
    } finally {
      setExitLoading(false);
    }
  };

  const [dateFilter, setDateFilter] = useState({
    beginDate: new Date(new Date().setMonth(new Date().getMonth() - 1))
      .toISOString()
      .split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  useEffect(() => {
    const saved = localStorage.getItem("studentTableOrder");
    if (saved) {
      try {
        setStudentColumns(JSON.parse(saved));
      } catch (e) {
        console.error(e);
      }
    }
  }, []);

  const saveOrder = () => {
    localStorage.setItem("studentTableOrder", JSON.stringify(studentColumns));
    setSaveStatus(true);
    setTimeout(() => setSaveStatus(false), 2000);
  };

  const handleDragStart = (event) =>
    setActiveCol(studentColumns.find((c) => c.id === event.active.id));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveCol(null);
    if (active.id !== over?.id) {
      setStudentColumns((cols) => {
        const oldIndex = cols.findIndex((c) => c.id === active.id);
        const newIndex = cols.findIndex((c) => c.id === over.id);
        return arrayMove(cols, oldIndex, newIndex);
      });
    }
  };

  const handleSelectStudent = (st, e) => {
    setPendingStudent((prev) => (e?.ctrlKey && prev?.id === st.id ? null : st));
  };

  const handleOpenMarks = () => {
    if (pendingStudent) setSelectedStudent(pendingStudent);
  };

  const fetchStudents = useCallback(
    async (filter) => {
      if (!groupId) return;
      setLoading(true);
      setError(null);
      try {
        const [res] = await Promise.all([
          apiFetch(
            BASE_URL + "/teacher-groups/get-teacher-one-group-students",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                groupId,
                status: filter === "Hamısı" ? "" : filter,
              }),
            },
          ),
          new Promise((r) => setTimeout(r, 600)),
        ]);
        const data = await res.json();
        setStudents(data.teacherGroupStudents || []);
      } catch (err) {
        setError("Məlumat yüklənmədi");
      } finally {
        setLoading(false);
      }
    },
    [groupId],
  );

  useEffect(() => {
    if (initialStudent) {
      setSelectedStudent(initialStudent);
    } else {
      setPendingStudent(null);
      setSelectedStudent(null);
      setMarks([]);
      if (groupId) fetchStudents(activeFilter);
    }
  }, [groupId, activeFilter, fetchStudents, initialStudent]);

  useEffect(() => {
    if (!selectedStudent) return;
    let isMounted = true;
    const fetchMarks = async () => {
      setMarksLoading(true);
      try {
        const endpoint =
          marksVersion === "v2"
            ? `${BASE_URL}/student-marks/mark-list/v2`
            : `${BASE_URL}/student-marks/mark-list`;
        const res = await apiFetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            beginDate: dateFilter.beginDate,
            endDate: dateFilter.endDate,
            contractId: selectedStudent.contractId,
          }),
        });
        const data = await res.json();
        if (isMounted) {
          setMarks(
            (data.marks || []).sort(
              (a, b) =>
                new Date(b.teacherSelectedDate) -
                new Date(a.teacherSelectedDate),
            ),
          );
        }
      } catch (err) {
        if (isMounted) {
          setError("Qiymətlər yüklənərkən xəta baş verdi");
        }
      } finally {
        if (isMounted) setMarksLoading(false);
      }
    };
    fetchMarks();
    return () => {
      isMounted = false;
    };
  }, [selectedStudent, dateFilter, marksVersion]);

  return (
    <div
      className={s?.tabWrapper}
      style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <DataLoader show={loading || marksLoading} />

      <div className={s?.tableToolbar}>
        <div className={s?.toolbarActionsLeft}>
          {selectedStudent ? (
            <button
              className={s?.iconActionBtnPurple}
              onClick={() => {
                setSelectedStudent(null);
                onBack?.();
              }}
              style={onBack ? { width: "auto", padding: "0 10px" } : {}}
            >
              <i className="bi bi-arrow-left"></i>
              {onBack && (
                <span style={{ fontSize: 13, marginLeft: 4, fontWeight: 600 }}>
                  Geri
                </span>
              )}
            </button>
          ) : (
            <>
              <button
                className={s?.iconActionBtnPurple}
                onClick={() => fetchStudents(activeFilter)}
              >
                <i className="bi bi-arrow-clockwise"></i>
              </button>

              <button
                className={s?.iconActionBtnPurple}
                disabled={!pendingStudent}
                onClick={handleOpenMarks}
                title="Tarixçə"
                style={{
                  marginLeft: "8px",
                  backgroundColor: pendingStudent
                    ? "#dbeafe"
                    : "transparent",
                  color: pendingStudent ? "var(--color-primary)" : "#9ca3af",
                  border: pendingStudent
                    ? "1px solid var(--color-primary)"
                    : "1px solid transparent",
                  opacity: pendingStudent ? 1 : 0.4,
                }}
              >
                <i className="bi bi-clock-history"></i>
              </button>

              <button
                className={s?.iconActionBtnPurple}
                disabled={!pendingStudent}
                onClick={() => setShowPassModal(true)}
                title="Şifrəni dəyiş"
                style={{
                  marginLeft: "6px",
                  backgroundColor: pendingStudent
                    ? "#fef3c7"
                    : "transparent",
                  color: pendingStudent ? "#f59e0b" : "#9ca3af",
                  border: pendingStudent
                    ? "1px solid #f59e0b"
                    : "1px solid transparent",
                  opacity: pendingStudent ? 1 : 0.4,
                }}
              >
                <i className="bi bi-key-fill"></i>
              </button>

              <button
                disabled={!pendingStudent || activeFilter !== "Passiv"}
                onClick={() => {
                  setExitReason("");
                  setShowExitModal(true);
                }}
                title={
                  activeFilter !== "Passiv"
                    ? "Yalnız Passiv filtrdə istifadə olunur"
                    : "Kursdan ayrılma səbəbi"
                }
                style={{
                  marginLeft: "6px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 32,
                  height: 32,
                  borderRadius: 6,
                  cursor:
                    pendingStudent && activeFilter === "Passiv"
                      ? "pointer"
                      : "not-allowed",
                  backgroundColor:
                    pendingStudent && activeFilter === "Passiv"
                      ? "#fee2e2"
                      : "transparent",
                  color:
                    pendingStudent && activeFilter === "Passiv"
                      ? "#ef4444"
                      : "#9ca3af",
                  border:
                    pendingStudent && activeFilter === "Passiv"
                      ? "1px solid #fca5a5"
                      : "1px solid #e5e7eb",
                  opacity:
                    pendingStudent && activeFilter === "Passiv" ? 1 : 0.4,
                  fontSize: 14,
                }}
              >
                <i className="bi bi-person-dash-fill"></i>
              </button>
            </>
          )}

          {selectedStudent ? (
            <span className="ms-2 fw-bold">
              {selectedStudent.name} {selectedStudent.surname}
            </span>
          ) : (
            <div className={s?.pillGroup}>
              {filterOptions.map((f) => (
                <button
                  key={f}
                  className={`${s?.pillBtn} ${activeFilter === f ? s?.pillActive : ""}`}
                  onClick={() => setActiveFilter(f)}
                >
                  {f}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="d-flex gap-2 align-items-center">
          {!selectedStudent && (
            <button
              onClick={openHelpModal}
              disabled={helpSelectedIds.size === 0}
              style={{
                background: "#0ea5e9",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                padding: "5px 12px",
                fontSize: 12,
                fontWeight: 700,
                cursor: helpSelectedIds.size === 0 ? "not-allowed" : "pointer",
                opacity: helpSelectedIds.size === 0 ? 0.45 : 1,
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              Kömək dərsi
              {helpSelectedIds.size > 0 && (
                <span
                  style={{
                    background: "#bfdbfe",
                    color: "#1d4ed8",
                    borderRadius: 10,
                    padding: "0 5px",
                    fontSize: 11,
                  }}
                >
                  {helpSelectedIds.size}
                </span>
              )}
            </button>
          )}
          {selectedStudent && (
            <>
              <button
                onClick={() => marksRefreshRef.current?.()}
                disabled={marksLoading}
                className="btn-save-icon-student"
                title="Yenilə"
              >
                <i
                  className={`bi bi-arrow-clockwise${marksLoading ? " spin-icon" : ""}`}
                ></i>
              </button>
              <button
                onClick={() => {
                  marksSaveRef.current?.();
                  setMarksSaveStatus(true);
                  setTimeout(() => setMarksSaveStatus(false), 2000);
                }}
                className={`btn-save-icon-student ${marksSaveStatus ? "active" : ""}`}
                title="Sıralamanı yadda saxla"
              >
                <i
                  className={`bi bi-${marksSaveStatus ? "check-lg" : "floppy-fill"}`}
                ></i>
              </button>
            </>
          )}
          {!selectedStudent && (
            <button
              onClick={saveOrder}
              className={`btn-save-icon-student ${saveStatus ? "active" : ""}`}
            >
              <i
                className={`bi bi-${saveStatus ? "check-lg" : "floppy-fill"}`}
              ></i>
            </button>
          )}
        </div>
      </div>

      <div
        ref={tableScrollRef}
        className={`${s?.tableScrollContainer || ""} tbl-scroll-wrap`}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {selectedStudent ? (
          <MarksPanel
            marks={marks}
            onFilterChange={(d) => setDateFilter(d)}
            currentDates={dateFilter}
            onCellClick={onCellClick}
            saveRef={marksSaveRef}
            refreshRef={marksRefreshRef}
            marksVersion={marksVersion}
            onVersionChange={setMarksVersion}
            loading={marksLoading}
            onApiError={handleApiError}
          />
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={studentColumns.map((c) => c.id)}
              strategy={horizontalListSortingStrategy}
            >
              <div style={{ display: "flex" }}>
                <div
                  style={{
                    minWidth: 36,
                    flexShrink: 0,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    style={{
                      padding: "10px 8px",
                      height: 40,
                      boxSizing: "border-box",
                      background: "var(--bg-elevated)",
                      borderBottom: "1px solid var(--border)",
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <input
                      type="checkbox"
                      style={{ accentColor: "#0ea5e9", cursor: "pointer" }}
                      checked={
                        students.length > 0 &&
                        students.every((st) => helpSelectedIds.has(st.id))
                      }
                      onChange={(e) =>
                        setHelpSelectedIds(
                          e.target.checked
                            ? new Set(students.map((st) => st.id))
                            : new Set(),
                        )
                      }
                    />
                  </div>
                  {students.map((st) => (
                    <div
                      key={st.id}
                      style={{
                        padding: "10px 8px",
                        height: 40,
                        boxSizing: "border-box",
                        borderBottom: "1px solid var(--bg-elevated)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: helpSelectedIds.has(st.id)
                          ? "#e0f2fe"
                          : "transparent",
                      }}
                    >
                      <input
                        type="checkbox"
                        style={{ accentColor: "#0ea5e9", cursor: "pointer" }}
                        checked={helpSelectedIds.has(st.id)}
                        onChange={() => toggleHelpSelect(st.id)}
                      />
                    </div>
                  ))}
                </div>
                {studentColumns.map((col) => (
                  <StudentSortableCol
                    key={col.id}
                    col={col}
                    students={students}
                    selectedStudent={pendingStudent}
                    onSelect={handleSelectStudent}
                    onCellClick={onCellClick}
                  />
                ))}
              </div>
            </SortableContext>
            <DragOverlay>
              {activeCol ? (
                <div className="p-2 bg-white border shadow">
                  {activeCol.label}
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>
      <div
        ref={customScrollRef}
        onScroll={handleCustomScrollX}
        className="custom-x-scrollbar"
        style={{
          overflowX: "auto",
          overflowY: "hidden",
          height: 10,
          flexShrink: 0,
        }}
      >
        <div style={{ width: tableScrollWidth, height: 1 }} />
      </div>

      {showHelpModal &&
        ReactDOM.createPortal(
          <div
            role="presentation"
            onClick={() => !helpLoading && closeHelpModal()}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 100050,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
              background: "rgba(15, 23, 42, 0.45)",
              backdropFilter: "blur(4px)",
              WebkitBackdropFilter: "blur(4px)",
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#fff",
                borderRadius: 12,
                boxShadow: "0 24px 64px rgba(0,0,0,0.2)",
                width: 800,
                maxWidth: "95vw",
                maxHeight: "85vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  background: "#f3f4f6",
                  borderBottom: "1px solid #e5e7eb",
                  padding: "12px 20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexShrink: 0,
                }}
              >
                <span
                  style={{ fontSize: 15, fontWeight: 700, color: "#374151" }}
                >
                  Kömək dərsi
                </span>
                {!helpLoading && (
                  <button
                    onClick={closeHelpModal}
                    style={{
                      border: "none",
                      background: "#e5e7eb",
                      borderRadius: 6,
                      width: 28,
                      height: 28,
                      fontSize: 18,
                      color: "#6b7280",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
              <div
                style={{
                  padding: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  flex: 1,
                }}
              >
                <div
                  style={{
                    padding: "16px 20px 12px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    flexShrink: 0,
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                      }}
                    >
                      <label
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#374151",
                          textTransform: "uppercase",
                          letterSpacing: "0.03em",
                        }}
                      >
                        Başlama tarixi
                      </label>
                      <input
                        type="datetime-local"
                        value={helpBeginDate}
                        onChange={(e) => setHelpBeginDate(e.target.value)}
                        style={{
                          padding: "7px 10px",
                          border: "1px solid #d1d5db",
                          borderRadius: 6,
                          fontSize: 12,
                          color: "#374151",
                          outline: "none",
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                      }}
                    >
                      <label
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#374151",
                          textTransform: "uppercase",
                          letterSpacing: "0.03em",
                        }}
                      >
                        Bitmə tarixi
                      </label>
                      <input
                        type="datetime-local"
                        value={helpEndDate}
                        onChange={(e) => setHelpEndDate(e.target.value)}
                        style={{
                          padding: "7px 10px",
                          border: "1px solid #d1d5db",
                          borderRadius: 6,
                          fontSize: 12,
                          color: "#374151",
                          outline: "none",
                        }}
                      />
                    </div>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                      }}
                    >
                      <label
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#374151",
                          textTransform: "uppercase",
                          letterSpacing: "0.03em",
                        }}
                      >
                        Dərs adı
                      </label>
                      <input
                        type="text"
                        value={helpLessonName}
                        onChange={(e) => setHelpLessonName(e.target.value)}
                        placeholder="Dərs mövzusu..."
                        style={{
                          padding: "7px 10px",
                          border: "1px solid #d1d5db",
                          borderRadius: 6,
                          fontSize: 12,
                          color: "#374151",
                          outline: "none",
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                      }}
                    >
                      <label
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#374151",
                          textTransform: "uppercase",
                          letterSpacing: "0.03em",
                        }}
                      >
                        Dərs növü
                      </label>
                      <select
                        value={helpLessonType}
                        onChange={(e) => setHelpLessonType(e.target.value)}
                        style={{
                          padding: "7px 10px",
                          border: "1px solid #d1d5db",
                          borderRadius: 6,
                          fontSize: 12,
                          color: "#374151",
                          outline: "none",
                          background: "#fff",
                        }}
                      >
                        <option value="Online">Online</option>
                        <option value="Offline">Offline</option>
                      </select>
                    </div>
                  </div>
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 4 }}
                  >
                    <label
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#374151",
                        textTransform: "uppercase",
                        letterSpacing: "0.03em",
                      }}
                    >
                      YouTube linki
                    </label>
                    <input
                      type="text"
                      value={helpYoutubeLink}
                      onChange={(e) => setHelpYoutubeLink(e.target.value)}
                      placeholder="https://youtube.com/..."
                      style={{
                        padding: "7px 10px",
                        border: "1px solid #d1d5db",
                        borderRadius: 6,
                        fontSize: 12,
                        color: "#374151",
                        outline: "none",
                      }}
                    />
                  </div>
                </div>
                <div
                  style={{
                    overflowY: "auto",
                    padding: "12px 20px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  {helpSelectedStudents.map((st) => (
                    <div
                      key={st.id}
                      style={{
                        background: "#f8fafc",
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        padding: "12px 14px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: "#374151",
                          marginBottom: 6,
                        }}
                      >
                        {st.name} {st.surname}
                        <span
                          style={{
                            fontSize: 11,
                            color: "#9ca3af",
                            fontWeight: 400,
                            marginLeft: 8,
                          }}
                        >
                          Müq. #{st.contractId}
                        </span>
                      </div>
                      <textarea
                        rows={2}
                        placeholder="Müəllim qeydi..."
                        value={helpNotes[st.contractId] || ""}
                        onChange={(e) =>
                          setHelpNotes((prev) => ({
                            ...prev,
                            [st.contractId]: e.target.value,
                          }))
                        }
                        style={{
                          width: "100%",
                          fontSize: 12,
                          border: "1px solid #d1d5db",
                          borderRadius: 6,
                          padding: "6px 8px",
                          resize: "vertical",
                          outline: "none",
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div
                style={{
                  background: "#f3f4f6",
                  borderTop: "1px solid #e5e7eb",
                  padding: "10px 16px",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                  flexShrink: 0,
                }}
              >
                <button
                  onClick={closeHelpModal}
                  disabled={helpLoading}
                  style={{
                    background: "#fff",
                    color: "#374151",
                    border: "1px solid #e5e7eb",
                    borderRadius: 6,
                    padding: "7px 16px",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Ləğv et
                </button>
                <button
                  onClick={handleHelpSubmit}
                  disabled={helpLoading || !helpBeginDate || !helpEndDate}
                  style={{
                    background: "#22c55e",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    padding: "7px 18px",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor:
                      helpLoading || !helpBeginDate || !helpEndDate
                        ? "not-allowed"
                        : "pointer",
                    opacity:
                      helpLoading || !helpBeginDate || !helpEndDate ? 0.6 : 1,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  {helpLoading ? (
                    "Göndərilir..."
                  ) : (
                    <>
                      <i className="bi bi-check-lg" /> Yadda saxla
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      <ValidationErrorModal
        show={!!helpErrorModal}
        onHide={() => setHelpErrorModal(null)}
        message={helpErrorModal?.message}
        validations={helpErrorModal?.validations}
      />

      <Modal
        show={showExitModal}
        onHide={() => setShowExitModal(false)}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 15 }}>
            <i className="bi bi-person-dash-fill me-2 text-danger"></i>
            Kursdan ayrılma səbəbi
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>
            <i className="bi bi-person-fill me-1"></i>
            {pendingStudent?.name} {pendingStudent?.surname}
          </div>
          <textarea
            className="form-control"
            rows={4}
            placeholder="Kursdan ayrılma səbəbini yazın..."
            value={exitReason}
            onChange={(e) => setExitReason(e.target.value)}
            style={{ fontSize: 13, resize: "none" }}
            autoFocus
          />
        </Modal.Body>
        <Modal.Footer style={{ gap: 8 }}>
          <button
            onClick={() => setShowExitModal(false)}
            style={{
              border: "1px solid #e5e7eb",
              background: "#f9fafb",
              borderRadius: 8,
              padding: "6px 16px",
              fontSize: 13,
              cursor: "pointer",
              color: "#374151",
            }}
          >
            Ləğv et
          </button>
          <button
            onClick={handleSubmitExitReason}
            disabled={!exitReason.trim() || exitLoading}
            style={{
              background: "#ef4444",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "6px 16px",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              opacity: !exitReason.trim() || exitLoading ? 0.5 : 1,
            }}
          >
            {exitLoading ? "Göndərilir..." : "Göndər"}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showPassModal}
        onHide={() => setShowPassModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16 }}>
            Şifrənin Yenilənməsi
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ChangePassword
            mode="student"
            targetData={pendingStudent}
            onComplete={() => setShowPassModal(false)}
            onApiError={handleApiError}
          />
        </Modal.Body>
      </Modal>

      <ToastContainer
        position="bottom-end"
        className="p-3"
        style={{ zIndex: 10000 }}
      >
        <Toast
          onClose={() => setShowToast(false)}
          show={showToast}
          delay={4000}
          autohide
          bg="danger"
        >
          <Toast.Header closeButton={false} className="bg-danger text-white">
            <strong className="me-auto">Sistem Xətası</strong>
          </Toast.Header>
          <Toast.Body className="text-white">{error}</Toast.Body>
        </Toast>
      </ToastContainer>

      <style>{`
        .help-modal-backdrop { background: #111827; }
        .btn-save-icon-student {
          width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;
          background: #2563eb; color: white; border: none; border-radius: 6px; cursor: pointer;
        }
        .btn-save-icon-student.active { background: #1d4ed8; }
        .tbl-scroll-wrap { scrollbar-width: thin; scrollbar-color: #93c5fd transparent; }
        .tbl-scroll-wrap::-webkit-scrollbar { width: 5px; height: 0; }
        .tbl-scroll-wrap::-webkit-scrollbar-track { background: transparent; }
        .tbl-scroll-wrap::-webkit-scrollbar-thumb { background: #93c5fd; border-radius: 4px; }
        .tbl-scroll-wrap::-webkit-scrollbar-thumb:hover { background: #60a5fa; }
        .custom-x-scrollbar { scrollbar-width: thin; scrollbar-color: #93c5fd #eff6ff; }
        .custom-x-scrollbar::-webkit-scrollbar { height: 6px; }
        .custom-x-scrollbar::-webkit-scrollbar-track { background: #eff6ff; border-radius: 3px; }
        .custom-x-scrollbar::-webkit-scrollbar-thumb { background: #93c5fd; border-radius: 3px; }
        .custom-x-scrollbar::-webkit-scrollbar-thumb:hover { background: #60a5fa; }
      `}</style>
      <ValidationErrorModal
        show={!!errorModal}
        onHide={clearError}
        message={errorModal?.message}
        validations={errorModal?.validations}
      />
    </div>
  );
}
